import { ChartSpecificColumnType, } from '@thoughtspot/ts-chart-sdk';
import { generateQueryParams, getExcludedFields, getFieldsByEncoding, getMeasureNameField, getUniqueQueries, partitionArrayByIndex, } from './fields';
import { getUniqueColumnsById } from './helpers';
function buildGreatGrandTotalsQuery(rowFields, colFields, otherFields) {
    const otherFieldsExceptExcludedRowFields = getExcludedFields(otherFields, [
        ...rowFields,
        ...colFields,
    ]);
    const measureNameField = getMeasureNameField([...rowFields, ...colFields]);
    const queries = [
        {
            queryColumns: measureNameField
                ? [...otherFieldsExceptExcludedRowFields, measureNameField]
                : otherFieldsExceptExcludedRowFields,
        },
    ];
    return queries.filter((query) => query.queryColumns.length);
}
function buildGreatSubTotalsQueries(rowFields, colFields, otherFields) {
    const queries = [];
    for (let i = 1; i < rowFields.length; i++) {
        for (let j = 1; j < colFields.length; j++) {
            if (rowFields[i].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES ||
                colFields[j].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
                continue;
            }
            const [rowQueries, excludedRowQueries] = partitionArrayByIndex(rowFields, i);
            const [colQueries, excludedColQueries] = partitionArrayByIndex(colFields, j);
            if (colQueries.length === 1 &&
                colQueries[0].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
                continue;
            }
            if (rowQueries.length === 1 &&
                rowQueries[0].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
                continue;
            }
            const otherFieldsExceptExcludedFields = getExcludedFields(otherFields, [
                ...excludedRowQueries,
                ...excludedColQueries,
            ]);
            const measureNameField = getMeasureNameField([
                ...excludedRowQueries,
                ...excludedColQueries,
            ]);
            const fields = [
                ...rowQueries,
                ...colQueries,
                ...(measureNameField ? [measureNameField] : []),
            ];
            queries.push({
                queryColumns: getUniqueColumnsById([...fields, ...otherFieldsExceptExcludedFields]),
            });
        }
    }
    return getUniqueQueries(queries);
}
function buildColumnSubTotalsQuery(rowFields, colFields, otherFields) {
    const queries = [];
    for (let i = rowFields.length - 1; i > 0; i--) {
        if (rowFields[i].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
            continue;
        }
        const [rowQueries, excludedFields] = partitionArrayByIndex(rowFields, i);
        if (rowQueries.length === 1 &&
            rowQueries[0].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
            continue;
        }
        const otherFieldsExceptExcludedRowFields = getExcludedFields(otherFields, excludedFields);
        const measureNameField = getMeasureNameField(excludedFields);
        queries.push({
            queryColumns: getUniqueColumnsById([
                ...rowQueries,
                ...colFields,
                ...otherFieldsExceptExcludedRowFields,
                ...(measureNameField ? [measureNameField] : []),
            ]),
        });
    }
    return getUniqueQueries(queries);
}
function buildRowSubTotalsQuery(rowFields, colFields, otherFields) {
    const queries = [];
    for (let i = colFields.length - 1; i > 0; i--) {
        if (colFields[i].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
            continue;
        }
        const [rowQueries, excludedFields] = partitionArrayByIndex(colFields, i);
        const otherFieldsExcludingColFields = getExcludedFields(otherFields, excludedFields);
        if (rowQueries.length === 1 &&
            rowQueries[0].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
            continue;
        }
        const measureNameField = getMeasureNameField(excludedFields);
        queries.push({
            queryColumns: getUniqueColumnsById([
                ...rowQueries,
                ...rowFields,
                ...otherFieldsExcludingColFields,
                ...(measureNameField ? [measureNameField] : []),
            ]),
        });
    }
    return getUniqueQueries(queries);
}
function buildColumnGrandTotalsQuery(rowFields, colFields, otherFields) {
    const queries = [];
    let isAllColsQuery = false;
    for (let i = 1; i < colFields.length; i++) {
        const [colQueries, excludedFields] = partitionArrayByIndex(colFields, i);
        if (colQueries.length === 1 &&
            colQueries[0].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
            continue;
        }
        const otherFieldsExceptExcludedFields = getExcludedFields(otherFields, [
            ...excludedFields,
            ...rowFields,
        ]);
        const isMeasureNameFieldPresentInColumn = excludedFields.find((field) => field.chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES);
        const measureNameField = getMeasureNameField([...excludedFields, ...rowFields]);
        const allColQueries = measureNameField ? [...colQueries, measureNameField] : [...colQueries];
        const totalColQueriesLength = isMeasureNameFieldPresentInColumn
            ? colQueries.length + 1
            : colQueries.length;
        if (totalColQueriesLength === colFields.length) {
            isAllColsQuery = true;
        }
        queries.push({
            queryColumns: getUniqueColumnsById([
                ...allColQueries,
                ...otherFieldsExceptExcludedFields,
            ]),
        });
    }
    if (!isAllColsQuery) {
        const fields = [...colFields, ...getExcludedFields(otherFields, rowFields)];
        const measureNameField = getMeasureNameField(rowFields);
        queries.push({
            queryColumns: getUniqueColumnsById([
                ...fields,
                ...(measureNameField ? [measureNameField] : []),
            ]),
        });
    }
    return getUniqueQueries(queries);
}
function buildRowGrandTotalsQuery(rowFields, colFields, otherFields) {
    const queries = [];
    let isAllRowsQuery = false;
    for (let i = 1; i < rowFields.length; i++) {
        if (rowFields[i].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
            continue;
        }
        const [rowQueries, excludedRowQueries] = partitionArrayByIndex(rowFields, i);
        const otherFieldsExceptExcludedFields = getExcludedFields(otherFields, [
            ...excludedRowQueries,
            ...colFields,
        ]);
        const isMeasureNameFieldPresentInColumn = excludedRowQueries.find((field) => field.chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES);
        const measureNameField = getMeasureNameField([...excludedRowQueries, ...colFields]);
        const allRowQueries = measureNameField ? [...rowQueries, measureNameField] : [...rowQueries];
        const totalRowQueriesLength = isMeasureNameFieldPresentInColumn
            ? rowQueries.length + 1
            : rowQueries.length;
        if (allRowQueries.length === 1 &&
            allRowQueries[0].chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES) {
            continue;
        }
        if (totalRowQueriesLength === rowFields.length) {
            isAllRowsQuery = true;
        }
        queries.push({
            queryColumns: getUniqueColumnsById([
                ...allRowQueries,
                ...otherFieldsExceptExcludedFields,
            ]),
        });
    }
    if (!isAllRowsQuery) {
        const fields = [...rowFields, ...getExcludedFields(otherFields, colFields)];
        const measureNameField = getMeasureNameField(colFields);
        queries.push({
            queryColumns: getUniqueColumnsById([
                ...fields,
                ...(measureNameField ? [measureNameField] : []),
            ]),
        });
    }
    return getUniqueQueries(queries);
}
export function buildColumnTotalsQuery(chartConfigs, queryParams, encodingKeys) {
    const { rowFields, colFields, otherFields } = getFieldsByEncoding(chartConfigs, encodingKeys);
    const columnSubTotalsQueries = buildColumnSubTotalsQuery(rowFields, colFields, otherFields);
    const greatGrandTotalsQueries = buildGreatGrandTotalsQuery(rowFields, colFields, otherFields);
    const columnGrandTotalsQueries = buildColumnGrandTotalsQuery(rowFields, colFields, otherFields);
    const greatSubTotalsQueries = buildGreatSubTotalsQueries(rowFields, colFields, otherFields);
    return {
        columnSubTotals: generateQueryParams(columnSubTotalsQueries, queryParams),
        columnGrandTotals: generateQueryParams(columnGrandTotalsQueries, queryParams),
        greatGrandTotals: generateQueryParams(greatGrandTotalsQueries, queryParams),
        greatSubTotals: generateQueryParams(greatSubTotalsQueries, queryParams),
    };
}
export function buildRowTotalsQuery(chartConfigs, queryParams, encodingKeys) {
    const { rowFields, colFields, otherFields } = getFieldsByEncoding(chartConfigs, encodingKeys);
    const rowSubTotalQueries = buildRowSubTotalsQuery(rowFields, colFields, otherFields);
    const rowGrandTotalQueries = buildRowGrandTotalsQuery(rowFields, colFields, otherFields);
    const greatGrandTotalQueries = !rowFields.length
        ? buildGreatGrandTotalsQuery(rowFields, colFields, otherFields)
        : [];
    const uniqueQueries = getUniqueQueries([...rowGrandTotalQueries, ...greatGrandTotalQueries]);
    return {
        rowSubTotals: generateQueryParams(rowSubTotalQueries, queryParams),
        rowGrandTotals: generateQueryParams(uniqueQueries, queryParams),
    };
}
export function shouldInjectOppositeTotals(grandTotalsEnabled, fields) {
    return (!grandTotalsEnabled &&
        fields.length > 1 &&
        fields[0]?.chartSpecificColumnType === ChartSpecificColumnType.MEASURE_NAMES);
}
//# sourceMappingURL=totals-queries.js.map